import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { GovTaskTypes } from "../../../../../../constants/enums";
import type { GovProjectMilestoneNote, GovProjectTaskEntity } from "../../../../../../models";
import { SharedFields } from "../../configs";
import { AccordionSectionConfigType, ListSectionConfigType, ScreenSectionConfigType } from "../../wf-difference.types";

const weightField: ScreenSectionConfigType = {
  field: "weight",
  label: "common.tables.weight",
  view: "normal",
};

const notesField = (field: string, label: string): ListSectionConfigType => ({
  field,
  label,
  view: "list",
  idKey: "id",
  derivedList: (data: unknown) => {
    const notes = (data ?? []) as GovProjectMilestoneNote[];
    return notes.filter((note) => note.status !== Constants.OBJECT_STATUS.REMOVE);
  },
  format: (note: GovProjectMilestoneNote) => note.text,
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as GovProjectMilestoneNote;
    const $o = $oldValue as GovProjectMilestoneNote;
    return $n?.text !== $o?.text;
  },
});

const mainAchievementsField = notesField("mainAchievements", "national_projects.comments");

const recommendationsField = notesField("recommendations", "kpi.recommendations");

const taskTypeField: ScreenSectionConfigType = {
  field: "taskType",
  label: "ai_projects.tasks.task_type",
  view: "normal",
};

const targetField: ScreenSectionConfigType = {
  field: "target",
  label: "data_entry.target",
  view: "normal",
  visibleIf: (data: { taskType: number }) => data.taskType === GovTaskTypes.TargetSponsorTask,
};

const aiKpiField: ScreenSectionConfigType = {
  field: "aiKpiId",
  label: "breadcrumbs.aikpi_module.list",
  view: "normal",
  format: (value: Record<string, string>) => AdaaHelper.getItemValueByToken(value, "aiKpiName"),
  visibleIf: (data: { taskType: number }) => data.taskType === GovTaskTypes.LinkedToAiKpi,
};

const involvedEntitiesField: ListSectionConfigType = {
  field: "involvedEntities",
  label: "common.form.label.entities",
  view: "list",
  idKey: "entityId",
  format: (entity: GovProjectTaskEntity) => AdaaHelper.getItemValueByToken(entity, "entityName"),
  visibleIf: (data: { taskType: number }) => data.taskType === GovTaskTypes.SimpleMultiEntitiesTask,
};

const hasEntitiesDiff = ($new: unknown, $old: unknown) => {
  const newIds = (($new ?? []) as GovProjectTaskEntity[]).map(({ entityId }) => entityId);
  const oldIds = (($old ?? []) as GovProjectTaskEntity[]).map(({ entityId }) => entityId);

  if (newIds.length !== oldIds.length) return true;
  return newIds.some((id) => !oldIds.includes(id));
};

const taskConfig = [
  ...SharedFields.name,
  ...SharedFields.description,
  SharedFields.startDate,
  SharedFields.endDate,
  weightField,
  taskTypeField,
  targetField,
  aiKpiField,
  involvedEntitiesField,
];

const milestoneTasks: AccordionSectionConfigType = {
  view: "accordion",
  field: "tasks",
  title: ($o) => AdaaHelper.getItemValueByToken($o, "name"),
  idKey: "id",
  config: taskConfig,
  label: "ai_projects.tasks.title_small",
  hasDiff: ($newValue: unknown, $oldValue: unknown) => {
    const $n = $newValue as Record<string, unknown>;
    const $o = $oldValue as Record<string, unknown>;
    const keys = [
      "id",
      "nameAE",
      "nameEN",
      "dscAE",
      "dscEN",
      "startDate",
      "endDate",
      "weight",
      "taskType",
      "target",
      "aiKpiId",
    ];

    if (keys.some((key) => $o?.[key] !== $n?.[key])) return true;
    return hasEntitiesDiff($n?.involvedEntities, $o?.involvedEntities);
  },
};

export const govMilestoneScreenConfig = {
  type: "gov-project",
  sections: [
    ...SharedFields.name,
    ...SharedFields.description,
    SharedFields.startDate,
    SharedFields.endDate,
    weightField,
    mainAchievementsField,
    recommendationsField,
    milestoneTasks,
  ],
};
